import {Manifest2004, Objective, RollupCondition, RollupRule, ScoNavItem2004, SequenceRule} from "./manifest2004";
import {AllScos2004Data, Sco2004Data} from "./api2004";
import {SSLAApiEvents} from "../scorm/events";
import {ActivityObjective} from "./model";
import {NavActions} from "../scorm/sn";
import {SSLAConfiguration} from "../config";

type StatusResponse = "valid" | "not_valid";
export const STATUS_VALID: StatusResponse = "valid";
export const STATUS_NOT_VALID: StatusResponse = "not_valid";

export enum RequestCategory {
    Navigation = 1,
    Sequencing,
    Termination,
    Delivery
}

enum Direction {
    None = "none",
    Forward = "forward",
    Backward = "backward"
}

export enum SeqActions {
    Start = "start",
    Previous = "previous",
    Continue = "continue",
    Retry = "retry",
    Exit = "exit",
    ResumeAll = "resume_all",
    Choice = "choice",
    Jump = "jump"
}

export enum TermActions {
    Exit = "exit",
    Abandon = "abandon",
    AbandonAll = "abandon_all",
    ExitAll = "exit_all",
    ExitParent = "exit_parent",
    SuspendAll = "suspend_all",
}

const RuleActions = {
    // Pre
    Skip: "skip",
    Disabled: "disabled",
    HiddenFromChoice: "hiddenFromChoice",
    StopForwardTraversal: "stopForwardTraversal",

    // Post
    ExitParent: "exitParent",
    ExitAll: "exitAll",
    Retry: "retry",
    RetryAll: "retryAll",
    Continue: "continue",
    Previous: "previous",

    // Exit
    Exit: "exit"
};

const RollupActions = {
    Satisfied: "satisfied",
    NotSatisfied: "notSatisfied",
    Completed: "completed",
    Incomplete: "incomplete"
};

export class Request {
    // To make class TS-distinct, do not remove.
    private rawRequest = true;
    // cat: Request category: Navigation, Sequencing, Termination, etc.
    // act: Specific action: Start (sequencing request), Exit (termination request), etc.
    constructor(public cat: RequestCategory, public actions: NavActions | TermActions | SeqActions | string,
                public target: string = null, public exception: string = null) {
    }
}

export class NavigationRequest {
    // To make class TS-distinct, do not remove.
    private navRequest = true;
    constructor(public actions: NavActions | TermActions | SeqActions | string,
                public target: string = null, public exception: string = null) {
    }
}

class SequencingRequest {
    // To make class TS-distinct, do not remove.
    private seqRequest = true;
    constructor(public actions: NavActions | TermActions | SeqActions | string,
                public target: string = null, public exception: string = null) {
    }
}

class TerminationRequest {
    // To make class TS-distinct, do not remove.
    private termRequest = true;
    constructor(public actions: NavActions | TermActions | SeqActions | string,
                public target: string = null, public exception: string = null) {
    }
}

class DeliveryRequest {
    // To make class TS-distinct, do not remove.
    private delRequest = true;
    constructor(public actions: NavActions | TermActions | SeqActions | string,
                public target: string = null, public exception: string = null) {
    }
}

class RequestFlow {
    constructor(
        public status: StatusResponse,
        public navReq: NavigationRequest,
        public seqReq?: SequencingRequest,
        public termReq?: TerminationRequest,
        public deliveryReq?: DeliveryRequest,
        // TODO Should target be nullable or just empty string?
        public target?: string,
        public endSequencingSession: boolean = false,
        public exception: string = null) {
    }

    toJSON() {
        let d: any = {};
        if (this.status) {
            d['status'] = this.status;
        }
        if (this.navReq) {
            d['navReq'] = this.navReq;
        }
        if (this.seqReq) {
            d['seqReq'] = this.seqReq;
        }
        if (this.termReq) {
            d['termReq'] = this.termReq;
        }
        if (this.deliveryReq) {
            d['deliveryReq'] = this.deliveryReq;
        }
        if (this.target) {
            d['target'] = this.target;
        }
        if (this.endSequencingSession) {
            d['endSequencingSession'] = this.endSequencingSession;
        }
        if (this.exception) {
            d['exception'] = this.exception;
        }
        return d;
    }
}

class TraversalReturn {
    constructor(
        public activity: ScoNavItem2004,
        public direction: Direction,
        public endSequencingSession: boolean = false,
        public exception: string = null
    ) {
    }
}

class FlowReturn {
    constructor(
        public activity: ScoNavItem2004,
        public deliverable: boolean,
        public endSequencingSession: boolean = false,
        public exception: string = null
    ) {
    }
}

export class DebugTree {
    root: DbgMethod
}

export type DbgEntry = DbgMethod | Dbg;

export class DbgMethod {
    public when: Date = new Date();
    public children: DbgEntry[] = [];
    public parent: DbgMethod;
    public key: string = "";
    public desc: string = "";
    public args: any[] = [];
    public returns: any = undefined;
    constructor(key: string, desc: string) {
        this.key = key;
        this.desc = desc;
    }

    addArg(arg: any) {
        if (arg === null) {
            this.args.push("null");
        }
        this.args.push(arg);
    }

    depth(): number {
        let d = 0;
        let x = this.parent;
        while (x) {
            d++;
            x = x.parent;
        }
        return d;
    }

    setArgs(args: any[]) {
        if (args) {
            this.args = args.slice();
        }
    }

    setRet(ret: any) {
        this.returns = ret;
    }

    toString(): string {
        let indent = "    ";
        let s = this.key + "(";
        for (let i=0; i < this.args.length; i++) {
            try {
                s += '' + JSON.stringify(this.args[i]);
            }
            catch (c) {
                s += ' <unable to serialize this param>';
            }
            if (i < this.args.length - 1) {
                s += ', ';
            }
        }
        try {
            s += ") => " + JSON.stringify(this.returns);
        }
        catch (c) {
            s += ' <unable to serialize return>';
        }
        for (let i=0; i < this.children.length; i++) {
            let next = this.children[i].toString();
            s += "\n" + next.replace(/^/gm, indent).replace(/\s+$/g, '');
        }
        return s;
    }
}

class Dbg {
    when: Date = new Date();
    constructor(public key: string, public msg: string) {
    }

    toString(): string {
        return this.key + ": " + this.msg + "\n";
    }
}

class ClassHelper {
    static getMethodName(obj: any, method: any): string {
        let methodName = null;
        Object.getOwnPropertyNames(obj).forEach(prop => {
            if (obj[prop] === method) {
                methodName = prop;
            }
        });

        if (methodName !== null) {
            return methodName;
        }

        let proto = Object.getPrototypeOf(obj);
        if (proto) {
            return ClassHelper.getMethodName(proto, method);
        }
        return null;
    }
}

export class SequencingProcess2004 {
    protected manifest: Manifest2004;
    protected scosData: AllScos2004Data;
    protected events: SSLAApiEvents;
    protected config: SSLAConfiguration;
    protected debug: DebugTree;
    protected debugCurrent: DbgEntry;
    public currentActivityId: string = null;
    protected _intCurrentActivityIsActive: boolean = false;
    // Made public to allow for initialization at times like Start.  Should consider an accessor.
    public suspendedActivityId: string = null;

    constructor(manifest: Manifest2004, scosData: AllScos2004Data, events: SSLAApiEvents, config: SSLAConfiguration) {
        this.manifest = manifest;
        this.scosData = scosData;
        this.events = events;
        this.config = config;
        this.events.commitPreSave.add(this.updateObjectives, this);
    }

    protected getActivity(actId: string): ScoNavItem2004 {
        return this.manifest.scosByIdentifiers[actId];
    }

    protected getCurrentActivityActive(): boolean {
        return this._intCurrentActivityIsActive;
    }

    protected setCurrentActivityActive(active: boolean) {
        this._intCurrentActivityIsActive = active;
    }

    updateObjectives(): void {
        // In the current SCO, any local objectives that have been updated
        // should be propagated to global objectives.
        // TODO: Is it possible that this could cause other objectives to need
        // updating, and should be looped?

        let activity: ScoNavItem2004 = this.getActivity(this.currentActivityId);
        let data = this.scosData.getOrCreate(this.currentActivityId).exportData(this.config, true);
        let keys: string[] = Object.keys(data.data);
        // TODO: Actual type for this.
        let tmpGlobalObj: ActivityObjective;
        let i: number = 0;

        if (activity.sequence.primaryObjective) {
            let seqObj = activity.sequence.primaryObjective;
            let objKeyPrefix: string = "";
            if (seqObj.mapInfos.length > 0) {
                for (let mapIdx: number = 0; mapIdx < seqObj.mapInfos.length; mapIdx++) {
                    let map = seqObj.mapInfos[mapIdx];
                    if (map.targetObjectiveId) {
                        if (!this.scosData.globalObjectives[map.targetObjectiveId]) {
                            this.scosData.globalObjectives[map.targetObjectiveId] = new ActivityObjective();
                        }
                        tmpGlobalObj = this.scosData.globalObjectives[map.targetObjectiveId];
                        // TODO: SCORM2004-3rd_ImpactSummary.pdf, 1.4-17: Mapping of Primary Objective Status from cmi.objectives and/or cmi.success_status/cmi.score.scaled Updates
                        // We're currently only doing the success_status part of the spec.
                        if (map.writeSatisfiedStatus) {
                            tmpGlobalObj.successStatus = this.scosData.getOrCreate(activity.identifier).get("cmi.success_status") as string;
                        }
                        if (map.writeNormalizedMeasure) {
                            tmpGlobalObj.scaled = this.scosData.getOrCreate(activity.identifier).get("cmi.score.scaled") as string;
                        }
                        this._debugEntry("updated primary objective <" + map.targetObjectiveId + ">", JSON.stringify(tmpGlobalObj));
                        // TODO: We don't yet support adlseq:objectives and all their additional options.  It's allowed on primary objectives by name mapping.
                    }
                }
            }
        }

        if (activity.sequence.objectives) {
            for (i = 0; i < activity.sequence.objectives.length; i++) {
                let seqObj = activity.sequence.objectives[i];
                let objKeyPrefix: string = "";
                if (seqObj.mapInfos.length > 0) {
                    // Get the matching objective.
                    for (let i: number = 0; i < keys.length; i++) {
                        if (data.data[keys[i]] == seqObj.objectiveId) {
                            // Found the local objective data.
                            objKeyPrefix = "cmi.objectives." + keys[i].split(".")[2] + ".";
                            break;
                        }
                    }

                    if (objKeyPrefix != "") {
                        for (let mapIdx: number = 0; mapIdx < seqObj.mapInfos.length; mapIdx++) {
                            let map = seqObj.mapInfos[mapIdx];
                            if (map.targetObjectiveId) {
                                if (!this.scosData.globalObjectives[map.targetObjectiveId]) {
                                    this.scosData.globalObjectives[map.targetObjectiveId] = new ActivityObjective();
                                }
                                tmpGlobalObj = this.scosData.globalObjectives[map.targetObjectiveId];
                                if (map.writeSatisfiedStatus) {
                                    tmpGlobalObj.successStatus = this.scosData.getOrCreate(activity.identifier).get(objKeyPrefix + "success_status") as string;
                                }
                                if (map.writeNormalizedMeasure) {
                                    tmpGlobalObj.scaled = this.scosData.getOrCreate(activity.identifier).get(objKeyPrefix + "scaled") as string;
                                }
                                this._debugEntry("updated secondary objective <" + map.targetObjectiveId + ">", JSON.stringify(tmpGlobalObj));
                                // TODO: We don't yet support adlseq:objectives and all their additional options.  Is that even allowed on primary objectives?
                            }
                        }
                    }
                }

            }
        }
    }

    protected currentActivity(): ScoNavItem2004 {
        if (!this.currentActivityId) {
            return null;
        }
        return this.manifest.scosByIdentifiers[this.currentActivityId];
    }

    do(navigationRequest: Request): string {
        let dbg = new DebugTree();
        this.debug = dbg;
        dbg.root = new DbgMethod("Root", "");
        this.debugCurrent = dbg.root;
        let res = this._debug(this.op_1, [navigationRequest]);
        // console.log(dbg.root.toString());
        return res;
    }

    protected _debug<R>(method: (...args: any[]) => R, args?: any[]): R {
        // console.log("_debug?", ClassHelper.getMethodName(this, method), args);
        // Stacktrace blow-up check.
        let c = 0;
        let depthCheck = (this.debugCurrent as DbgMethod);
        while (depthCheck.parent) {
            c += 1;
            depthCheck = depthCheck.parent;
            if (c > 100) {
                console.log("DEPTH EXPLOSION, ABORTING!");
                throw new Error("Depth explosion in sequencer.");
            }
        }

        let dbg = new DbgMethod(ClassHelper.getMethodName(this, method), "");
        dbg.setArgs(args);
        (this.debugCurrent as DbgMethod).children.push(dbg);
        dbg.parent = (this.debugCurrent as DbgMethod);
        this.debugCurrent = dbg;
        let ret = method.apply(this, args);
        dbg.setRet(ret);
        this.debugCurrent = dbg.parent;
        return ret;
    }

    protected _debugEntry(key: string, msg: string) {
        let de = new Dbg(key, msg);
        (this.debugCurrent as DbgMethod).children.push(de);
    }

    protected _debugBool(b: boolean): string {
        return b ? "true" : "false";
    }

    // Overall Sequencing Process [OP.1]
    op_1(navigationRequest: Request): string {
        let flow: RequestFlow = this._debug(this.nb_2_1, [navigationRequest]);

        if (flow.termReq) {
            // 1.3.1
            let termFlow: RequestFlow = this._debug(this.tb_2_3, [flow.termReq]);
            if (termFlow.status == STATUS_NOT_VALID) {
                // 1.3.2.2
                return
            }
            // 1.3.3
            if (termFlow.seqReq) {
                // 1.3.3.1
                flow.seqReq = termFlow.seqReq;
            }
        }

        if (flow.seqReq) {
            flow = this._debug(this.sb_2_12, [flow.seqReq]);
            if (flow.status == STATUS_NOT_VALID) {
                return "";
            }
            if (flow.endSequencingSession) {
                return "";
            }
            if (!flow.deliveryReq) {
                return "";
            }
        }

        if (flow.deliveryReq) {
            let flowCheck: RequestFlow = this._debug(this.db_1_1, [flow.deliveryReq]);
            if (flowCheck.status == STATUS_NOT_VALID) {
                return "";
            }

            this._debug(this.db_2, [flow.deliveryReq]);
            return flow.deliveryReq.target;
        }

        return "";
    }

    // Navigation Request Process [NB.2.1]
    nb_2_1(navigationRequest: NavigationRequest): RequestFlow {
        // 1
        if (navigationRequest.actions == NavActions.Start) {
            if (!this.currentActivityId) {
                return new RequestFlow(STATUS_VALID, null,
                    new SequencingRequest(SeqActions.Start), null);
            }
            else {
                return new RequestFlow(STATUS_NOT_VALID, null,
                    null, null, null, "", false,"NB.2.1-1");
            }
        }

        // 2
        if (navigationRequest.actions == NavActions.ResumeAll) {
            if (!this.currentActivityId) {
                if (this.suspendedActivityId) {
                    return new RequestFlow(STATUS_VALID, null,
                        new SequencingRequest(SeqActions.ResumeAll), null, null);
                }
                else {
                    return new RequestFlow(STATUS_NOT_VALID, null,
                        null, null, null, "", false, "NB.2.1-3");
                }
            }
            else {
                return new RequestFlow(STATUS_NOT_VALID, null,
                    null, null, null, "", false, "NB.2.1-1");
            }
        }

        // 3
        if (navigationRequest.actions == NavActions.Continue) {
            // 3.1
            if (!this.currentActivityId) {
                return new RequestFlow(STATUS_NOT_VALID, null,
                    null, null, null, "", false, "NB.2.1-2");
            }

            // 3.2
            if (this.manifest.itemTree.identifier != this.currentActivityId &&
                (this.manifest.scosByIdentifiers[this.currentActivityId].parent).sequence.controlMode.flow) {
                // 3.2.1
                if (this.getCurrentActivityActive()) {
                    return new RequestFlow(STATUS_VALID, null, new SequencingRequest(SeqActions.Continue),
                        new TerminationRequest(TermActions.Exit));
                }
                // 3.2.2
                else {
                    return new RequestFlow(STATUS_VALID, null, new SequencingRequest(SeqActions.Continue));
                }
            }
            // 3.3
            else {
                return new RequestFlow(STATUS_NOT_VALID, null, null, null, null, "", false, "NB.2.1-4");
            }
        }

        // 4
        if (navigationRequest.actions == NavActions.Previous) {
            if (!this.currentActivityId) {
                return new RequestFlow(STATUS_NOT_VALID, null,
                    null, null, null, "", false, "NB.2.1-2");
            }

            if (this.manifest.itemTree.identifier != this.currentActivityId) {
                // 4.2.1
                let parentActivity = this.currentActivity().parent;
                if (parentActivity.sequence.controlMode.flow && !parentActivity.sequence.controlMode.forwardOnly) {
                    // 4.2.1.1
                    if (this.getCurrentActivityActive()) {
                        // 4.2.1.1.1
                        return new RequestFlow(STATUS_VALID, null, new SequencingRequest(SeqActions.Previous),
                            new TerminationRequest(TermActions.Exit));
                    } else {
                        // 4.2.1.2.1
                        return new RequestFlow(STATUS_VALID, null, new SequencingRequest(SeqActions.Previous));
                    }

                }
                // 4.2.2
                else {
                    return new RequestFlow(STATUS_NOT_VALID, null, null, null, null, "", false, "NB.2.1-5");
                }
            } else {
                // 4.3.1
                return new RequestFlow(STATUS_NOT_VALID, null, null, null, null, "", false, "NB.2.1-6");
            }
        }

        // 5
        if (navigationRequest.actions == NavActions.Forward) {
            return new RequestFlow(STATUS_NOT_VALID, null,
                null, null, null, "", false, "NB.2.1-7");
        }

        // 6
        if (navigationRequest.actions == NavActions.Backward) {
            return new RequestFlow(STATUS_NOT_VALID, null,
                null, null, null, "", false, "NB.2.1-7");
        }

        // 7
        if (navigationRequest.actions == NavActions.Choice) {
            // 7.1
            if (this.manifest.scosByIdentifiers[navigationRequest.target]) {
                // 7.1.1
                if (this.manifest.itemTree.identifier == navigationRequest.target ||
                    (this.manifest.scosByIdentifiers[navigationRequest.target].parent).sequence.controlMode.choice) {
                    // 7.1.1.1
                    if (!this.currentActivityId) {
                        // 7.1.1.1.1
                        return new RequestFlow(STATUS_VALID, null,
                            new SequencingRequest(SeqActions.Choice, navigationRequest.target),
                            null, null, navigationRequest.target);
                    }
                    // 7.1.1.2
                    let isSiblings = this.manifest.isSiblings(this.currentActivityId, navigationRequest.target);
                    this._debugEntry("7.1.1.2", "Are " + this.currentActivityId + " and " + navigationRequest.target + " siblings? " + this._debugBool(isSiblings));
                    if (!isSiblings) {
                        // 7.1.1.2.1
                        const commonAncestorId = this.manifest.commonAncestor(this.currentActivityId, navigationRequest.target);
                        // 7.1.1.2.3
                        if (commonAncestorId && commonAncestorId != this.currentActivityId) {
                            let tmp = this.currentActivity();
                            while (tmp.identifier != commonAncestorId) {
                                // 7.1.1.2.3.1.1
                                if (this.getCurrentActivityActive() && !tmp.sequence.controlMode.choiceExit) {
                                    // 7.1.1.2.3.1.1.1
                                    return new RequestFlow(STATUS_NOT_VALID, null,
                                        null, null, null, "", false, "NB.2.1-8");
                                }
                                tmp = tmp.parent;
                            }
                        }
                        else {
                            // 7.1.1.2.4.1
                            return new RequestFlow(STATUS_NOT_VALID, null,
                                null, null, null, "", false, "NB.2.1-9");
                        }
                    }
                    // 7.1.1.3
                    this._debugEntry("7.1.1.3", "Activity is Active for the Current Activity is True And the Sequencing Control Choice Exit for the Current Activity is False");
                    this._debugEntry("7.1.1.3", "Activity is Active for the Current Activity = " + this._debugBool(this.getCurrentActivityActive()));
                    this._debugEntry("7.1.1.3", "the Sequencing Control Choice Exit for the Current Activity = " + this._debugBool(this.currentActivity().sequence.controlMode.choiceExit));
                    if (this.getCurrentActivityActive() && !this.currentActivity().sequence.controlMode.choiceExit) {
                        // 7.1.1.3.1
                        return new RequestFlow(STATUS_NOT_VALID, null,
                            null, null, null, "", false, "NB.2.1-8");
                    }
                    // 7.1.1.4
                    if (this.getCurrentActivityActive()) {
                        // 7.1.1.4.1
                        return new RequestFlow(STATUS_VALID, null,
                            new SequencingRequest(SeqActions.Choice, navigationRequest.target),
                            new TerminationRequest(TermActions.Exit, navigationRequest.target), null,
                            navigationRequest.target);
                    } else {
                        // 7.1.1.5.1
                        return new RequestFlow(STATUS_VALID, null,
                            new SequencingRequest(SeqActions.Choice, navigationRequest.target),
                            null, null, navigationRequest.target);
                    }
                }
                // 7.1.2
                else {
                    // 7.1.2.1
                    return new RequestFlow(STATUS_NOT_VALID, null,
                        null, null, null, "", false, "NB.2.1-10");
                }
            }
            // 7.2
            else {
                // 7.2.1
                return new RequestFlow(STATUS_NOT_VALID, null,
                    null, null, null, "", false, "NB.2.1-11");
            }
        }

        // 8
        if (navigationRequest.actions == NavActions.Exit) {
            // 8.1
            if (this.currentActivityId) {
                // 8.1.1
                if (this.getCurrentActivityActive()) {
                    // 8.1.1.1
                    return new RequestFlow(STATUS_VALID, null,
                        new SequencingRequest(SeqActions.Exit),
                        new TerminationRequest(TermActions.Exit));
                }
                // 8.1.2
                else {
                    // 8.1.2.1
                    return new RequestFlow(STATUS_NOT_VALID, null,
                        null, null, null, "", false, "NB.2.1-12");
                }
            }
            // 8.2
            else {
                // 8.2.1
                return new RequestFlow(STATUS_NOT_VALID, null,
                    null, null, null, "", false, "NB.2.1-2");
            }
        }

        // 9
        if (navigationRequest.actions == NavActions.ExitAll) {
            // 9.1
            if (this.currentActivityId) {
                return new RequestFlow(STATUS_VALID, null,
                    new SequencingRequest(SeqActions.Exit),
                    new TerminationRequest(TermActions.ExitAll));
            }
            else {
                // 9.2.1
                return new RequestFlow(STATUS_NOT_VALID, null,
                    null, null, null, "", false, "NB.2.1-2");
            }
        }

        // 10
        if (navigationRequest.actions == NavActions.Abandon) {
            // 10.1
            if (this.currentActivityId) {
                // 10.1.1
                if (this.getCurrentActivityActive()) {
                    // 10.1.1.1
                    return new RequestFlow(STATUS_VALID, null,
                        new SequencingRequest(SeqActions.Exit),
                        new TerminationRequest(TermActions.Abandon));
                }
                // 10.1.2
                else {
                    // 10.1.2.1
                    return new RequestFlow(STATUS_NOT_VALID, null,
                        null, null, null, "", false, "NB.2.1-12");
                }
            }
            // 10.2
            else {
                // 10.2.1
                return new RequestFlow(STATUS_NOT_VALID, null,
                    null, null, null, "", false, "NB.2.1-2");
            }
        }

        // 11
        if (navigationRequest.actions == NavActions.AbandonAll) {
            // 11.1
            if (this.currentActivityId) {
                return new RequestFlow(STATUS_VALID, null,
                    new SequencingRequest(SeqActions.Exit),
                    new TerminationRequest(TermActions.AbandonAll));
            }
            else {
                // 11.2.1
                return new RequestFlow(STATUS_NOT_VALID, null,
                    null, null, null, "", false, "NB.2.1-2");
            }
        }

        // 12
        if (navigationRequest.actions == NavActions.SuspendAll) {
            // 12.1
            if (this.currentActivityId) {
                return new RequestFlow(STATUS_VALID, null,
                    new SequencingRequest(SeqActions.Exit),
                    new TerminationRequest(TermActions.SuspendAll));
            }
            else {
                // 12.2.1
                return new RequestFlow(STATUS_NOT_VALID, null,
                    null, null, null, "", false, "NB.2.1-2");
            }
        }

        // 13
        return new RequestFlow(STATUS_NOT_VALID, null,
            null, null, null, "", false, "NB.2.1-13");
    }

    // Sequencing Exit Action Rules Subprocess [TB.2.1]
    protected tb_2_1(activity: ScoNavItem2004): void {
        // 1
        let scos: ScoNavItem2004[] = activity.getLineage() as ScoNavItem2004[];
        // 2
        let exitTarget: ScoNavItem2004 = null;
        // 3
        // "from the root of the activity tree to the parent of the Current Activity, inclusive"
        for (let i: number = 0; i < scos.length - 1; i++) {
            // 3.1
            let res: string = this.up_2(scos[i], [SeqActions.Exit]);
            // 3.2
            if (res !== null) {
                // 3.2.1
                exitTarget = scos[i];
                // 3.2.2
                break;
            }
        }
        // 4
        if (exitTarget) {
            // 4.1
            this.up_3(activity);
            // 4.2
            this.up_4(activity);
            // 4.3
            this.currentActivityId = exitTarget.identifier;
        }
    }

    // Sequencing Post Condition Rules Subprocess [TB.2.2]
    protected tb_2_2(activity: ScoNavItem2004): RequestFlow {
        // 1
        if (this.scosData.getOrCreate(activity.identifier).get("cmi.exit") == "suspend") {
            // 1.1
            return new RequestFlow(STATUS_VALID, null, null, null);
        }
        // 2
        let actionRes: string = this.up_2(activity, [
            RuleActions.ExitParent,
            RuleActions.ExitAll,
            RuleActions.Retry,
            RuleActions.RetryAll,
            RuleActions.Continue,
            RuleActions.Previous
        ]);
        // 3
        if (actionRes) {
            // 3.1
            if (actionRes == RuleActions.Retry || actionRes == RuleActions.Continue || actionRes == RuleActions.Previous) {
                // 3.1.1
                // TODO: There's a type mismatch with actionRes, we should make sure it's a SeqActions.
                return new RequestFlow(STATUS_VALID, null, new SequencingRequest(actionRes), null);
            }
            // 3.2
            else if (actionRes == RuleActions.ExitParent || actionRes == RuleActions.ExitAll) {
                // 3.2.1
                // TODO: There's a type mismatch with actionRes, we should make sure it's a SeqActions.
                return new RequestFlow(STATUS_VALID, null, null, new TerminationRequest(actionRes));
            }
            // 3.3
            else if (actionRes == RuleActions.RetryAll) {
                // 3.3.1
                // TODO: There's a type mismatch with actionRes, we should make sure it's a SeqActions.
                return new RequestFlow(STATUS_VALID, null, new SequencingRequest(SeqActions.Retry), new TerminationRequest(TermActions.ExitAll));
            }
        }
        // 4
        return new RequestFlow(STATUS_VALID, null, null, null);
    }

    // Termination Request Process [TB.2.3]
    protected tb_2_3(termRequest: Request): RequestFlow {
        let rf: RequestFlow = null;

        // 1
        if (!this.currentActivityId) {
            // 1.1
            return new RequestFlow(STATUS_NOT_VALID, null, null, null, null, null, null, "TB.2.3-1");
        }

        // 2
        if (!this.getCurrentActivityActive() &&
            (termRequest.actions == TermActions.Exit || termRequest.actions == TermActions.Abandon)) {
            // 2.1
            return new RequestFlow(STATUS_NOT_VALID, null, null, null, null, null, null, "TB.2.3-2");
        }

        let currentActivity = this.currentActivity();
        // 3
        if (termRequest.actions == TermActions.Exit) {
            this._debug(this.up_4, [currentActivity]);
            this._debug(this.tb_2_1, [currentActivity]);
            // 3.3
            let processedExit = true;
            let fallthrough = false;
            while (!processedExit) {
                // 3.3.1
                processedExit = false;
                // 3.3.2
                rf = this._debug(this.tb_2_2, [currentActivity]);
                // 3.3.3
                if (rf.termReq && rf.termReq.actions == TermActions.ExitAll) {
                    termRequest.actions = TermActions.ExitAll;
                    // Fall through to the exit all code.
                    fallthrough = true;
                    break;
                }

                // 3.3.4
                if (rf.termReq && rf.termReq.actions == TermActions.ExitParent) {
                    if (this.manifest.scosByIdentifiers[this.currentActivityId].parent) {
                        // 3.3.4.1.1
                        this.currentActivityId = currentActivity.parent.identifier;
                        // 3.3.4.1.2
                        this._debug(this.up_4, [currentActivity]);
                        // 3.3.4.1.3
                        processedExit = true;
                    }
                    else {
                        return new RequestFlow(STATUS_NOT_VALID, null, null, null, null, null, null, "TB.2.3-4");
                    }
                }
                // 3.3.5
                else {
                    if (!currentActivity.parent &&
                        rf.seqReq.actions != SeqActions.Retry) {
                        return new RequestFlow(STATUS_VALID, null, new SequencingRequest(SeqActions.Exit), null);
                    }
                }
            }

            if (!fallthrough) {
                // 3.5
                return new RequestFlow(STATUS_VALID, null, rf ? rf.seqReq : null, null, null);
            }
        }

        // 4
        if (termRequest.actions == TermActions.ExitAll) {
            if (this.getCurrentActivityActive()) {
                // 4.1.1
                this._debug(this.up_4, [currentActivity]);
            }
            // 4.2
            this._debug(this.up_3, [this.manifest.itemTree]);
            // 4.3
            this._debug(this.up_4, [currentActivity]);
            // 4.4
            this.currentActivityId = this.manifest.itemTree.identifier;
            // 4.5
            return new RequestFlow(STATUS_VALID, null, new SequencingRequest(SeqActions.Exit), null);
        }

        // 5
        if (termRequest.actions == TermActions.SuspendAll) {
            // TODO This is the wrong way to check for suspended.  Suspended should be checked by the sco data, probably cmi.exit = "suspend".  Check the SCORM book to verify this definition.
            // 5.1
            if (this.getCurrentActivityActive() || this.suspendedActivityId == this.currentActivityId) {
                // 5.1.1
                this._debug(this.rb_1_5, [this.currentActivity()]);
                // 5.1.2
                this.suspendedActivityId = this.currentActivityId;
            }
            // 5.2
            else {
                // 5.2.1
                if (this.currentActivityId != this.manifest.itemTree.identifier) {
                    this.suspendedActivityId = this.currentActivity().parent.identifier;
                }
                // 5.2.2
                else {
                    return new RequestFlow(STATUS_NOT_VALID, null, null, null, null, null, null, "TB.2.3-3");
                }
            }
            // 5.3 - 5.7
            // Since only one activity can be active and only one can be suspended, short-circuit the flow of this.
            this.currentActivityId = this.manifest.itemTree.identifier;
            return new RequestFlow(STATUS_VALID, null, new SequencingRequest(SeqActions.Exit), null);
        }

        // 6
        if (termRequest.actions == TermActions.Abandon) {
            this.setCurrentActivityActive(false);
            return new RequestFlow(STATUS_VALID, null, null, null);
        }

        // 7
        if (termRequest.actions == TermActions.SuspendAll) {
            // Since only one activity can be active in SCORM at a time, this is technically correct, and the
            // other rules for 7 shouldn't apply.
            this.setCurrentActivityActive(false);
            this.currentActivityId = this.manifest.itemTree.identifier;
            return new RequestFlow(STATUS_VALID, null, null, null);
        }

        return new RequestFlow(STATUS_NOT_VALID, null, null, null, null, null, null, "TB.2.3-7");
    }

    // Measure Rollup Process [RB.1.1 a]
    protected rb_1_1a(activity: ScoNavItem2004): void {
        let childObjective: Objective = null;
        let tmpSco: ScoNavItem2004 = null;

        // 1
        let totalMeasure: number = 0.0;
        // 2
        let validData: boolean = false;
        // 3
        let countedMeasures: number = 0.0;
        // 4 - 5.1.2
        let targetObjective: Objective = activity.sequence.primaryObjective;
        // 6
        if (targetObjective) {
            // 6.1
            for (let i: number = 0; i < activity.children.length; i++) {
                tmpSco = activity.getKid(i);
                childObjective = null;
                // 6.1.1
                if (tmpSco.sequence.deliveryControls.tracked) {
                    // 6.1.1.1 - 6.1.1.2.1.2
                    childObjective = tmpSco.sequence.primaryObjective;
                }

                // 6.1.1.3
                if (childObjective) {
                    // 6.1.1.3.1
                    countedMeasures += tmpSco.sequence.rollup.objectiveMeasureWeight;
                    // 6.1.1.3.2
                    let scaled = this.scosData.getOrCreate(tmpSco.identifier).get("cmi.score.scaled");
                    if (scaled) {
                        // 6.1.1.3.2.1
                        totalMeasure += tmpSco.sequence.rollup.objectiveMeasureWeight * parseFloat(scaled as string);
                    }
                    // 6.1.1.3.2.2
                    validData = true;
                }
                // 6.1.1.4
                else {
                    // 6.1.1.4.1
                    return;
                }
            }

            // 6.2
            if (!validData) {
                // 6.2.1
                this.scosData.getOrCreate(activity.identifier).set("cmi.score.scaled", "");
            }
            // 6.3
            else {
                // 6.3.1
                if (countedMeasures > 0.0) {
                    // 6.3.1.1 - 6.3.1.2
                    this.scosData.getOrCreate(activity.identifier).set(
                        "cmi.score.scaled",
                        String(totalMeasure / countedMeasures)
                    );
                }
                // 6.3.2
                else {
                    // 6.3.2.1
                    this.scosData.getOrCreate(activity.identifier).set("cmi.score.scaled", "");
                }
            }
        }
    }

    // Completion Measure Rollup Process [RB.1.1 b]
    protected rb_1_1b(activity: ScoNavItem2004): void {
        let tmpSco: ScoNavItem2004 = null;
        // 1
        let totalMeasure: number = 0.0;
        // 2
        let validData: boolean = false;
        // 3
        let countedMeasures: number = 0.0;

        // 4
        for (let i: number = 0; i < activity.children.length; i++) {
            tmpSco = activity.getKid(i);
            // 4.1
            if (tmpSco.sequence.deliveryControls.tracked) {
                // 4.1.1
                if (tmpSco.completionThreshold && tmpSco.completionThreshold.progressWeight) {
                    countedMeasures += tmpSco.completionThreshold.progressWeight;
                    // 4.1.2
                    let progressMeasure = this.scosData.getOrCreate(tmpSco.identifier).get("cmi.progress_measure");
                    if (progressMeasure) {
                        // 4.1.2.1
                        totalMeasure += parseFloat(progressMeasure as string) * tmpSco.completionThreshold.progressWeight;
                        // 4.1.2.2
                        validData = true;
                    }
                }
            }
        }

        // 5 - 5.2
        if (validData) {
            // 5.2.1
            if (countedMeasures > 0.0) {
                // 5.2.1.1 - 5.2.1.2
                this.scosData.getOrCreate(activity.identifier).set("cmi.progress_measure", String(totalMeasure / countedMeasures));
            }
            else {
                // 5.2.2.1
                this.scosData.getOrCreate(activity.identifier).set("cmi.progress_measure", "");
            }
        }
    }

    protected setRollupAndLocalObjectiveSuccess(obj: Objective, act: ScoNavItem2004, val: string): void {
        act.rollupObjective.successStatus = val;
        // Empty objective ID means there's no local objective to write this to.
        if (obj.objectiveId != "") {
            this.scosData.getOrCreate(act.identifier).set("cmi.objectives.0.success_status", val);
        }
        // TODO: Is it wrong to update the actual cmi.success_status?  This function
        // is only called on the primary objective, so it should be speaking
        // for the SCO as a whole.  Adding it in until I understand why it's wrong
        // to do so.
        this.scosData.getOrCreate(act.identifier).set("cmi.success_status", val);
    }

    // Objective Rollup Using Measure Process [RB.1.2 a]
    protected rb_1_2a(activity: ScoNavItem2004): void {
        // 1 - 2.1.2
        let targetObjective: Objective = activity.sequence.primaryObjective;
        //let scaled = this.scosData.getOrCreate(activity.identifier).get("cmi.objectives.0.score.scaled");
        // TODO This should select from the objective scaled score OR the activity scaled score,
        // based on the precedence rules in table 4.5.4a in the SN book.
        let scaled = this.scosData.getOrCreate(activity.identifier).get("cmi.score.scaled");

        // 3
        // An activity always has a primary objective, even if it's implicit.
        // See https://www.imsglobal.org/simplesequencing/ssv1p0/imsss_bestv1p0.html
        // under Glossary - objectives:
        // "Each activity must have at least one primary objective".
        // An implicit primary objective just won't have a local or global
        // objective that it reads from/writes to.  There still has to be a
        // data model objective on the activity itself, to store state.
        if (targetObjective) {
            // 3.1
            if (targetObjective.satisfiedByMeasure) {
                // 3.1.1
                if (!scaled) {
                    // 3.1.1.1
                    this.setRollupAndLocalObjectiveSuccess(targetObjective, activity, "unknown");
                }
                // 3.1.2
                else {
                    // 3.1.2.1
                    if (!this.getCurrentActivityActive() || (
                        activity.identifier == this.currentActivityId &&
                        activity.sequence.rollupConsiderations.measureSatisfactionIfActive)) {
                        // 3.1.2.1.1
                        if (scaled >= activity.sequence.primaryObjective.minNormalizedMeasure) {
                            // 3.1.2.1.1.1 - 3.1.2.1.1.2
                            this.setRollupAndLocalObjectiveSuccess(targetObjective, activity, "passed");
                        }
                        else {
                            // 3.1.2.1.2.1 - 3.1.2.1.2.2
                            this.setRollupAndLocalObjectiveSuccess(targetObjective, activity, "failed");
                        }
                    }
                    // 3.1.2.2
                    else {
                        // 3.1.2.2.1
                        this.setRollupAndLocalObjectiveSuccess(targetObjective, activity, "unknown");
                    }
                }
            }
        }
    }

    // Objective Rollup Using Rules Process [RB.1.2 b]
    protected rb_1_2b(activity: ScoNavItem2004): void {
        // 1
        // TODO: The rule reads:
        // "If the activity does not include Rollup Rules with the Not Satisfied
        // rollup action And the activity does not include Rollup Rules with the
        // Satisfied rollup action Then"
        // But rule 4.1 and 4.3 seem to assume both not satisfied and satisfied
        // rules exist.  What if only one existed before this check?  Should
        // the other one be created?  Is this "AND" not a logical and?
        let rollupActionFound: boolean = false;
        for (let i: number = 0; i < activity.sequence.rollup.rules.length; i++) {
            let action: string = activity.sequence.rollup.rules[i].action;
            if (action == RollupActions.Satisfied || action == RollupActions.NotSatisfied) {
                rollupActionFound = true;
                break;
            }
        }
        if (!rollupActionFound) {
            // 1.1
            let rr = new RollupRule();
            let rc = new RollupCondition();
            rr.action = RollupActions.Satisfied;
            // TODO: Const this.
            rr.childActivitySet = "all";
            rc.condition = "satisfied";
            rr.conditions.push(rc);
            activity.sequence.rollup.rules.push(rr);

            // 1.2
            rr = new RollupRule();
            rc = new RollupCondition();
            rr.action = RollupActions.NotSatisfied;
            // TODO: Const this.
            rr.childActivitySet = "all";
            rc.condition = "objectiveStatusKnown";
            rr.conditions.push(rc);
            activity.sequence.rollup.rules.push(rr);
        }

        // 2 - 3.1.2
        let targetObjective: Objective = activity.sequence.primaryObjective;
        // 4
        // An activity always has a primary objective, even if it's implicit.
        // See rb_1_2a for details.
        if (targetObjective) {
            // 4.1
            let rollupCheck: boolean = this._debug(this.rb_1_4, [activity, RollupActions.NotSatisfied]);
            // 4.2
            if (rollupCheck) {
                // 4.2.1 - 4.2.2
                this.setRollupAndLocalObjectiveSuccess(targetObjective, activity, "failed");
            }
            // 4.3
            rollupCheck = this._debug(this.rb_1_4, [activity, RollupActions.Satisfied]);
            // 4.4
            if (rollupCheck) {
                // 4.4.1 - 4.4.2
                this.setRollupAndLocalObjectiveSuccess(targetObjective, activity, "passed");
            }
        }
    }

    // Activity Progress Rollup Using Measure Process [RB.1.3 a]
    protected rb_1_3a(activity: ScoNavItem2004): void {
        // TODO: Should this be explicitly overwritten right here?
        // Attempt Progress Status and Attempt Completion Status supposedly map to this.
        // 1 - 2
        this.scosData.getOrCreate(activity.identifier).set("cmi.completion_status", "incomplete");
        // 3
        if (activity.completionThreshold.completedByMeasure) {
            // 3.1
            let progressMeasure: string = this.scosData.getOrCreate(activity.identifier).get("cmi.progress_measure") as string;
            if (!progressMeasure) {
                // 3.1.1
                // TODO: Should this be unknown or incomplete?
                this.scosData.getOrCreate(activity.identifier).set("cmi.completion_status", "incomplete");
            }
            // 3.2
            else {
                // 3.2.1
                if (parseFloat(progressMeasure) > activity.completionThreshold.minProgressMeasure) {
                    // 3.2.1.1 - 3.2.1.2
                    this.scosData.getOrCreate(activity.identifier).set("cmi.completion_status", "completed");
                }
                else {
                    // 3.2.2.1 - 3.2.2.2
                    this.scosData.getOrCreate(activity.identifier).set("cmi.completion_status", "incomplete");
                }
            }
        }
        // 4
        else {
            // 4.1
            // TODO: Do we overwrite this, or just leave it alone?
            this.scosData.getOrCreate(activity.identifier).set("cmi.completion_status", "unknown");
        }
    }

    // Activity Progress Rollup Using Rules Process [RB.1.3 b]
    protected rb_1_3b(activity: ScoNavItem2004): void {
        // 1
        // TODO: The same issue as rb_1_2b.
        let rollupActionFound: boolean = false;
        for (let i: number = 0; i < activity.sequence.rollup.rules.length; i++) {
            let action: string = activity.sequence.rollup.rules[i].action;
            if (action == RollupActions.Completed || action == RollupActions.Incomplete) {
                rollupActionFound = true;
                break;
            }
        }
        if (!rollupActionFound) {
            // 1.1
            let rr = new RollupRule();
            let rc = new RollupCondition();
            rr.action = RollupActions.Completed;
            // TODO: Const this.
            rr.childActivitySet = "all";
            rc.condition = "completed";
            rr.conditions.push(rc);
            activity.sequence.rollup.rules.push(rr);

            // 1.2
            rr = new RollupRule();
            rc = new RollupCondition();
            rr.action = RollupActions.Incomplete;
            // TODO: Const this.
            rr.childActivitySet = "all";
            rc.condition = "activityProgressKnown";
            rr.conditions.push(rc);
            activity.sequence.rollup.rules.push(rr);
        }

        // 2
        let rollupCheck: boolean = this._debug(this.rb_1_4, [activity, RollupActions.Incomplete]);
        // 3
        if (rollupCheck) {
            // 3.1 - 3.2
            this.scosData.getOrCreate(activity.identifier).set("cmi.completion_status", "incomplete");
        }
        // 4
        rollupCheck = this._debug(this.rb_1_4, [activity, RollupActions.Completed]);
        // 5
        if (rollupCheck) {
            // 5.1 - 5.2
            this.scosData.getOrCreate(activity.identifier).set("cmi.completion_status", "completed");
        }
    }

    // Rollup Rule Check Subprocess [RB.1.4]
    protected rb_1_4(activity: ScoNavItem2004, rollupAction: string): boolean {
        // 1 - 1.1
        let rollupActionFound: boolean = false;
        let rules: RollupRule[] = [];
        let rule: RollupRule = null;
        let i: number = 0, j: number = 0;
        let rollupAnswers: boolean[];
        for (i = 0; i < activity.sequence.rollup.rules.length; i++) {
            if (activity.sequence.rollup.rules[i].action == rollupAction) {
                rules.push(activity.sequence.rollup.rules[i]);
            }
        }
        if (rules.length > 0) {
            // 1.2
            for (i = 0; i < rules.length; i++) {
                rule = rules[i];
                // 1.2.1
                rollupAnswers = [];
                // 1.2.2
                for (j = 0; j < activity.children.length; j++) {
                    let child: ScoNavItem2004 = activity.getKid(j);
                    // 1.2.2.1
                    if (child.sequence.deliveryControls.tracked) {
                        // 1.2.2.1.1
                        let childRollupResult: boolean = this._debug(this.rb_1_4_2, [child, rule.action]);
                        // 1.2.2.1.2
                        if (childRollupResult === true) {
                            // 1.2.2.1.2.1 - 1.2.2.1.2.3.2.1
                            rollupAnswers.push(this._debug(this.rb_1_4_1, [child, rule]));
                        }
                    }
                }

                // 1.2.3
                let statusChanged: boolean = false;
                // 1.2.4
                if (rollupAnswers.length > 0) {
                    // 1.2.5
                    if (rule.childActivitySet == "all") {
                        // 1.2.5.1
                        statusChanged = true;
                        for (j = 0; j < rollupAnswers.length; j++) {
                            if (rollupAnswers[j] !== true) {
                                statusChanged = false;
                                break;
                            }
                        }
                    }
                    // 1.2.6
                    else if (rule.childActivitySet == "any") {
                        // 1.2.6.1
                        for (j = 0; j < rollupAnswers.length; j++) {
                            if (rollupAnswers[j] === true) {
                                statusChanged = true;
                                break;
                            }
                        }
                    }
                    // 1.2.7
                    else if (rule.childActivitySet == "none") {
                        // 1.2.7.1
                        statusChanged = true;
                        for (j = 0; j < rollupAnswers.length; j++) {
                            if (rollupAnswers[j] !== false) {
                                statusChanged = false;
                                break;
                            }
                        }
                    }
                    // 1.2.8
                    else if (rule.childActivitySet == "atLeastCount") {
                        // 1.2.8.1
                        let rollupCount: number = 0;
                        for (j = 0; j < rollupAnswers.length; j++) {
                            if (rollupAnswers[j] === true) {
                                rollupCount += 1;
                            }
                        }
                        if (rollupCount >= rule.minimumCount) {
                            statusChanged = true;
                        }
                    }
                    // 1.2.9
                    else if (rule.childActivitySet == "atLeastPercent") {
                        // 1.2.9.1
                        let rollupCount: number = 0;
                        for (j = 0; j < rollupAnswers.length; j++) {
                            if (rollupAnswers[j] === true) {
                                rollupCount += 1;
                            }
                        }
                        if ((rollupCount / rollupAnswers.length) >= rule.minimumPercent) {
                            statusChanged = true;
                        }
                    }
                }

                // 1.2.10
                if (statusChanged) {
                    // 1.2.10.1
                    return true;
                }
            }
        }
        return false;
    }

    protected globalIdFromLocalObjective(localObj: Objective): string {
        if (localObj) {
            if (localObj.mapInfos.length > 0) {
                return localObj.mapInfos[0].targetObjectiveId;
            }
        }
        return "";
    }

    protected globalIdFromPrimaryObjective(activity: ScoNavItem2004): string {
        return this.globalIdFromLocalObjective(activity.sequence.primaryObjective);
    }

    // Evaluate Rollup Conditions Subprocess [RB.1.4.1]
    protected rb_1_4_1(activity: ScoNavItem2004, rule: RollupRule): boolean {
        // returning null for "unknown".
        let i: number = 0;
        // 1
        let rollupResults: boolean[] = [];
        // 2
        for (let i: number = 0; i < rule.conditions.length; i++) {
            let result: boolean = null;
            // 2.1

            // TODO Since we're in rollup, this should be checking the activity level, right?
            // Verify this.
            // Really it should be checking Objective Status Whatever, but we
            // don't maintain separate states for that at this point.
            if (rule.conditions[i].condition == "satisfied") {
                // let globalObjectiveId: string = this.globalIdFromPrimaryObjective(activity);
                // if (globalObjectiveId) {
                //     if (this.scosData.globalObjectives[globalObjectiveId].successStatus == "passed") {
                //         result = true;
                //     }
                //     else {
                //         result = false;
                //     }
                // }

                let status: string = this.scosData.getOrCreate(activity.identifier).get("cmi.success_status") as string;
                if (status == "passed") {
                    result = true;
                }
                else {
                    result = false;
                }
            }
            else if (rule.conditions[i].condition == "objectiveStatusKnown") {
                // let globalObjectiveId: string = this.globalIdFromPrimaryObjective(activity);
                // if (globalObjectiveId) {
                //     let status: string = this.scosData.globalObjectives[globalObjectiveId].successStatus;
                //     if (status == "passed" || status == "failed") {
                //         result = true;
                //     }
                //     else {
                //         result = false;
                //     }
                // }

                let status: string = this.scosData.getOrCreate(activity.identifier).get("cmi.success_status") as string;
                if (status == "passed" || status == "failed") {
                    result = true;
                }
                else {
                    result = false;
                }
            }
            else if (rule.conditions[i].condition == "objectiveMeasureKnown") {
                // let globalObjectiveId: string = this.globalIdFromPrimaryObjective(activity);
                // if (globalObjectiveId) {
                //     let measure: string = this.scosData.globalObjectives[globalObjectiveId].scaled;
                //     if (measure != "") {
                //         result = true;
                //     }
                //     else {
                //         result = false;
                //     }
                // }

                let measure: string = this.scosData.getOrCreate(activity.identifier).get("cmi.score.scaled") as string;
                if (measure != "") {
                    result = true;
                }
                else {
                    result = false;
                }
            }
            else if (rule.conditions[i].condition == "completed") {
                // let globalObjectiveId: string = this.globalIdFromPrimaryObjective(activity);
                // if (globalObjectiveId) {
                //     let status: string = this.scosData.globalObjectives[globalObjectiveId].completionStatus;
                //     if (status == "completed") {
                //         result = true;
                //     }
                //     else {
                //         result = false;
                //     }
                // }

                let status: string = this.scosData.getOrCreate(activity.identifier).get("cmi.completion_status") as string;
                if (status == "completed") {
                    result = true;
                }
                else {
                    result = false;
                }
            }
            else if (rule.conditions[i].condition == "activityProgressKnown") {
                // let globalObjectiveId: string = this.globalIdFromPrimaryObjective(activity);
                // if (globalObjectiveId) {
                //     let status: string = this.scosData.globalObjectives[globalObjectiveId].completionStatus;
                //     if (status == "completed" || status == "incomplete") {
                //         result = true;
                //     }
                //     else {
                //         result = false;
                //     }
                // }

                let status: string = this.scosData.getOrCreate(activity.identifier).get("cmi.completion_status") as string;
                if (status == "completed" || status == "incomplete") {
                    result = true;
                }
                else {
                    result = false;
                }
            }
            else if (rule.conditions[i].condition == "attempted") {
                // let globalObjectiveId: string = this.globalIdFromPrimaryObjective(activity);
                // if (globalObjectiveId) {
                //     let status: string = this.scosData.globalObjectives[globalObjectiveId].completionStatus;
                //     if (status == "completed" || status == "incomplete") {
                //         result = true;
                //     }
                //     else {
                //         result = false;
                //     }
                // }

                let status: string = this.scosData.getOrCreate(activity.identifier).get("cmi.completion_status") as string;
                if (status == "completed" || status == "incomplete") {
                    result = true;
                }
                else {
                    result = false;
                }
            }
            // TODO The SN and the CAM book disagree on the existence of this.
            else if (rule.conditions[i].condition == "attemptLimitExceeded") {
                // TODO
            }
            // TODO The SN and the CAM book disagree on the existence of this.
            else if (rule.conditions[i].condition == "timeLimitExceeded") {
                // TODO
            }
            // TODO The SN and the CAM book disagree on the existence of this.
            else if (rule.conditions[i].condition == "outsideAvailableTimeRange") {
                // TODO
            }
            // TODO The SN and the CAM book disagree on the existence of this.
            else if (rule.conditions[i].condition == "never") {
                result = false;
            }
                // TODO The SN and the CAM book don't disagree on the existence of this,
            // but if never maybe exists, does this?
            else if (rule.conditions[i].condition == "always") {
                result = true;
            }

            // 2.2
            if (rule.conditions[i].operator == "not") {
                // 2.2.1
                if (result === true) {
                    result = false;
                }
                else if (result === false) {
                    result = true;
                }
            }

            // 2.3
            rollupResults.push(result);
        }

        // 3
        if (rollupResults.length == 0) {
            // 3.1
            return null;
        }
        // 4
        if (rule.conditionCombination == "all") {
            for (i = 0; i < rollupResults.length; i++) {
                if (rollupResults[i] !== true) {
                    return false;
                }
            }
            return true;
        }
        else if (rule.conditionCombination == "any") {
            for (i = 0; i < rollupResults.length; i++) {
                if (rollupResults[i] === true) {
                    return true;
                }
            }
            return false;
        }
    }

    // Check Child for Rollup Subprocess [RB.1.4.2]
    protected rb_1_4_2(activity: ScoNavItem2004, rollupAction: string): boolean {
        // 1
        let included: boolean = false;
        // 2
        if (rollupAction == RollupActions.Satisfied || rollupAction == RollupActions.NotSatisfied) {
            // 2.1
            if (activity.sequence.rollup.rollupObjectiveSatisfied) {
                // 2.1.1
                included = true;
                // 2.1.2
                if ((rollupAction == RollupActions.Satisfied && activity.sequence.rollupConsiderations.requiredForSatisfied == "ifNotSuspended") ||
                    (rollupAction == RollupActions.NotSatisfied && activity.sequence.rollupConsiderations.requiredForNotSatisfied == "ifNotSuspended")) {
                    // 2.1.2.1
                    // TODO Exclude suspended activities 2.1.2.1(.1)
                }
                // 2.1.3
                else {
                    // 2.1.3.1
                    if ((rollupAction == RollupActions.Satisfied && activity.sequence.rollupConsiderations.requiredForSatisfied == "ifAttempted") ||
                        (rollupAction == RollupActions.NotSatisfied && activity.sequence.rollupConsiderations.requiredForNotSatisfied == "ifAttempted")) {
                        // 2.1.3.1.1
                        // TODO Exclude suspended activities 2.1.3.1.1(.1)
                    }
                    // 2.1.3.2
                    else {
                        // 2.1.3.2.1
                        if ((rollupAction == RollupActions.Satisfied && activity.sequence.rollupConsiderations.requiredForSatisfied == "ifNotSkipped") ||
                            (rollupAction == RollupActions.NotSatisfied && activity.sequence.rollupConsiderations.requiredForNotSatisfied == "ifNotSkipped")) {
                            // 2.1.3.2.1.1 - 2.1.3.2.1.2
                            if (this._debug(this.up_2, [activity, ["skipped"]]) != "unknown") {
                                // 2.1.3.2.1.2.1
                                included = false;
                            }
                        }

                    }
                }
            }
        }
        // 3
        else if (rollupAction == RollupActions.Completed || rollupAction == RollupActions.Incomplete) {
            // 3.1
            if (activity.sequence.rollup.rollupProgressCompletion) {
                // 3.1.1
                included = true;
                // 3.1.2
                if ((rollupAction == RollupActions.Completed && activity.sequence.rollupConsiderations.requiredForCompleted == "ifNotSuspended") ||
                    (rollupAction == RollupActions.Incomplete && activity.sequence.rollupConsiderations.requiredForIncomplete == "ifNotSuspended")) {
                    // 3.1.2.1
                    // TODO Exclude suspended activities 3.1.2.1(.1)
                }
                // 3.1.3
                else {
                    // 3.1.3.1
                    if ((rollupAction == RollupActions.Completed && activity.sequence.rollupConsiderations.requiredForCompleted == "ifAttempted") ||
                        (rollupAction == RollupActions.Incomplete && activity.sequence.rollupConsiderations.requiredForIncomplete == "ifAttempted")) {
                        // 3.1.3.1.1
                        // TODO Exclude suspended activities 2.1.3.1.1(.1)
                    }
                    // 3.1.3.2
                    else {
                        // 3.1.3.2.1
                        if ((rollupAction == RollupActions.Completed && activity.sequence.rollupConsiderations.requiredForCompleted == "ifNotSkipped") ||
                            (rollupAction == RollupActions.Incomplete && activity.sequence.rollupConsiderations.requiredForIncomplete == "ifNotSkipped")) {
                            // 3.1.3.2.1.1 - 3.1.3.2.1.2
                            if (this._debug(this.up_2, [activity, ["skipped"]]) != "unknown") {
                                // 3.1.3.2.1.2.1
                                included = false;
                            }
                        }
                    }
                }
            }
        }
        return included;
    }

    // TODO The rollup rules need a proper fork between 3rd and 4th Edition.  This satisfies both, but
    //   is trickier to step through if you're following the flowchart.
    // Overall Rollup Process [RB.1.5]
    protected rb_1_5(activity: ScoNavItem2004): void {
        // 1
        let activityList: ScoNavItem2004[] = activity.getLineage() as ScoNavItem2004[];
        activityList.reverse();
        // 2
        if (activityList.length == 0) {
            return;
        }
        // 3
        for (let i: number = 0; i < activityList.length; i++) {
            // 3.1
            if (!activityList[i].isLeaf) {
                // 3.1.1
                this._debug(this.rb_1_1a, [activityList[i]]);
                // 3.1.2
                this._debug(this.rb_1_1b, [activityList[i]]);
            }
            // 3.2
            this._debug(this.rb_1_2a, [activityList[i]]);
            this._debug(this.rb_1_2b, [activityList[i]]);
            // 3.3
            this._debug(this.rb_1_3a, [activityList[i]]);
            this._debug(this.rb_1_3b, [activityList[i]]);
        }
    }

    // Select Children Process [SR.1]
    // Randomize Children Process [SR.2]

    // Flow Tree Traversal Subprocess [SB.2.1]
    protected sb_2_1(activity: ScoNavItem2004, direction: Direction,
           previousDirection: Direction, considerChildren: boolean): TraversalReturn {
        let reversedDirection: boolean = false;
        let parentAvailKids: ScoNavItem2004[];
        if (activity.parent) {
            parentAvailKids = activity.parent.availableChildren() as ScoNavItem2004[];
            if (previousDirection == Direction.Backward &&
                parentAvailKids[parentAvailKids.length - 1].identifier == activity.identifier
            ) {
                // 2.1
                direction = Direction.Backward;
                activity = parentAvailKids[0];
                reversedDirection = true;
            }
        }

        if (direction == Direction.Forward) {
            if (activity.identifier == this.manifest.activityOrdered[this.manifest.activityOrdered.length - 1].identifier ||
                (!considerChildren && activity.identifier == this.manifest.itemTree.identifier)
            ) {
                // 3.1.1
                this._debug(this.up_3, [activity]);
                // 3.1.2
                return new TraversalReturn(null, Direction.None, true, "");
            }

            if (activity.isLeaf || !considerChildren) {
                if (activity.identifier == parentAvailKids[parentAvailKids.length - 1].identifier) {
                    return this._debug(this.sb_2_1, [activity.parent, direction, Direction.None, considerChildren]);
                }
                else {
                    for (let i: number = 0; i < parentAvailKids.length; i++) {
                        if (parentAvailKids[i].identifier == activity.identifier) {
                            return new TraversalReturn(parentAvailKids[i + 1], direction);
                        }
                    }
                }
            }
            else {
                let kids: ScoNavItem2004[] = activity.availableChildren() as ScoNavItem2004[];
                if (kids.length > 0) {
                    return new TraversalReturn(kids[0], direction);
                }
                return new TraversalReturn(null, direction, false, "SB.2.1-2");
            }
        }
        // 4
        else if (direction == Direction.Backward) {
            if (activity.identifier == this.manifest.itemTree.identifier) {
                // 4.1.1
                return new TraversalReturn(null, direction, false, "SB.2.1-3");
            }
            // 4.2
            if (activity.isLeaf || !considerChildren) {
                // 4.2.1
                if (!reversedDirection) {
                    // 4.2.1.1
                    if (activity.parent.sequence.controlMode.forwardOnly) {
                        return new TraversalReturn(null, direction, false, "SB.2.1-4");
                    }
                }
                // 4.2.2
                if (parentAvailKids[0].identifier == activity.identifier) {
                    return this._debug(this.sb_2_1, [activity.parent, Direction.Backward, Direction.None, false]);
                }
                // 4.2.3
                else {
                    for (let i: number = parentAvailKids.length - 1; i >= 0; i--) {
                        // 4.2.3.1
                        if (parentAvailKids[i].identifier == activity.identifier) {
                            return new TraversalReturn(parentAvailKids[i - 1], direction);
                        }
                    }
                }
            }
            // 4.3
            else {
                let availKids = activity.availableChildren() as ScoNavItem2004[];
                // 4.3.1
                if (availKids.length > 0) {
                    // 4.3.1.1
                    if (activity.sequence.controlMode.forwardOnly) {
                        return new TraversalReturn(availKids[0], Direction.Forward);
                    }
                    // 4.3.1.2
                    else {
                        return new TraversalReturn(availKids[availKids.length - 1], Direction.Backward);
                    }
                }
                // 4.3.2
                else {
                    return new TraversalReturn(null, direction, false, "SB.2.1-2");
                }
            }
        }
    }

    // Flow Activity Traversal Subprocess [SB.2.2]
    protected sb_2_2(activity: ScoNavItem2004, direction: Direction, previousDirection: Direction): FlowReturn {
        let tr: TraversalReturn;
        if (!((activity.parent).sequence.controlMode.flow)) {
            return new FlowReturn(activity, false, true, "SB.2.2-1");
        }
        // TODO Should "skipped" be RuleActions.Skip (or a new RuleActions.Skipped?)
        let skipResult: string = this._debug(this.up_2, [activity, ["skipped"]]);
        if (skipResult != null) {
            tr = this._debug(this.sb_2_1, [activity, direction, previousDirection, false]);
            if (tr.activity == null) {
                return new FlowReturn(activity, false, tr.endSequencingSession, tr.exception);
            }

            if (previousDirection == Direction.Backward && tr.direction == Direction.Backward) {
                tr = this._debug(this.sb_2_1, [tr.activity, direction, Direction.None, true]);
            }
            else {
                tr = this._debug(this.sb_2_1, [tr.activity, direction, previousDirection, true]);
            }
            return new FlowReturn(tr.activity, tr.activity == null ? true : false, tr.endSequencingSession, tr.exception);
        }

        if (this._debug(this.up_5, [activity])) {
            return new FlowReturn(activity, false, false, "SB.2.2-2");
        }

        if (!activity.isLeaf) {
            tr = this._debug(this.sb_2_1, [tr.activity, direction, Direction.None, true]);
            if (tr.activity == null) {
                return new FlowReturn(activity, false, tr.endSequencingSession, tr.exception);
            }

            if (direction == Direction.Backward && tr.direction == Direction.Forward) {
                tr = this._debug(this.sb_2_1, [tr.activity, Direction.Forward, Direction.Backward, true]);
            }
            else {
                tr = this._debug(this.sb_2_1, [tr.activity, direction, Direction.None, true]);
            }
            return new FlowReturn(tr.activity, tr.activity == null ? true : false, tr.endSequencingSession, tr.exception);
        }

        return new FlowReturn(activity, true);
    }

    // Flow Subprocess [SB.2.3]
    protected sb_2_3(activity: ScoNavItem2004, direction: Direction, considerChildren: boolean): FlowReturn {
        let tr: TraversalReturn = this._debug(this.sb_2_1, [activity, direction, Direction.None, considerChildren]);
        if (!tr.activity) {
            return new FlowReturn(activity, false, tr.endSequencingSession, tr.exception);
        }
        else {
            return this._debug(this.sb_2_2, [tr.activity, direction, Direction.None]);
        }
    }

    // Choice Activity Traversal Subprocess [SB.2.4]
    protected sb_2_4(activity: ScoNavItem2004, direction: Direction): {reachable: boolean, exception: string} {
        if (direction == Direction.Forward) {
            // 1.1
            let res = this._debug(this.up_2, [activity, [RuleActions.StopForwardTraversal]]);
            if (res != null) {
                // 1.2.1
                return {reachable: false, exception: "SB.2.4-1"};
            }
            return {reachable: true, exception: ""};
        }
        else if (direction == Direction.Backward) {
            // 2.1 (but backwards)
            if (!activity.parent) {
                // 2.2.1
                return {reachable: false, exception: "SB.2.4-3"};
            }

            // 2.1.1
            if ((activity.parent).sequence.controlMode.forwardOnly) {
                // 2.1.1.1
                return {reachable: false, exception: "SB.2.4-2"};
            }
            return {reachable: true, exception: ""};
        }
    }

    // Start Sequencing Request Process [SB.2.5]
    protected sb_2_5(): RequestFlow {
        if (this.currentActivityId) {
            return new RequestFlow(null, null, null, null, null, null, false, "SB.2.5-1");
        }
        if (this.manifest.itemTree.children.length == 0) {
            return new RequestFlow(null, null, null, null, new DeliveryRequest("", this.manifest.activityOrdered[0].identifier), null, false, "");
        }

        let fr: FlowReturn = this._debug(this.sb_2_3, [this.manifest.itemTree, Direction.Forward, true]);
        if (fr.exception) {
            return new RequestFlow(null, null, null, null, null, null, fr.endSequencingSession, fr.exception);
        }
        return new RequestFlow(null, null, null, null, new DeliveryRequest("", fr.activity.identifier));
    }

    // Resume All Sequencing Request Process [SB.2.6]
    protected sb_2_6(): RequestFlow {
        if (this.currentActivityId) {
            return new RequestFlow(null, null, null, null, null, null, false, "SB.2.6-1");
        }
        if (!this.suspendedActivityId) {
            return new RequestFlow(null, null, null, null, null, null, false, "SB.2.6-2");
        }
        return new RequestFlow(null, null, null, null, new DeliveryRequest("", this.suspendedActivityId));
    }

    // Continue Sequencing Request Process [SB.2.7]
    protected sb_2_7(): RequestFlow {
        if (!this.currentActivityId) {
            return new RequestFlow(null, null, null, null, null, null, false, "SB.2.7-1");
        }
        if (this.manifest.activityOrdered.length > 0 &&
            this.manifest.activityOrdered[0].identifier == this.currentActivityId) {
            if (!((this.manifest.activityOrdered[0].parent).sequence.controlMode.flow)) {
                return new RequestFlow(null, null, null, null, null, null, false, "SB.2.7-2");
            }
        }

        let fr: FlowReturn = this._debug(this.sb_2_3, [this.manifest.scosByIdentifiers[this.currentActivityId], Direction.Forward, false]);
        if (fr.exception) {
            return new RequestFlow(null, null, null, null, null, null, fr.endSequencingSession, fr.exception);
        }
        return new RequestFlow(null, null, null, null, new DeliveryRequest("", fr.activity.identifier));
    }

    // Previous Sequencing Request Process [SB.2.8]
    protected sb_2_8(): RequestFlow {
        if (!this.currentActivityId) {
            return new RequestFlow(null, null, null, null, null, null, false, "SB.2.8-1");
        }
        if (this.manifest.activityOrdered.length > 0 &&
            this.manifest.activityOrdered[0].identifier == this.currentActivityId) {
            if (!((this.manifest.activityOrdered[0].parent).sequence.controlMode.flow)) {
                return new RequestFlow(null, null, null, null, null, null, false, "SB.2.8-2");
            }
        }

        let fr: FlowReturn = this._debug(this.sb_2_3, [this.manifest.scosByIdentifiers[this.currentActivityId], Direction.Backward, false]);
        if (fr.exception) {
            return new RequestFlow(null, null, null, null, null, null, fr.endSequencingSession, fr.exception);
        }
        return new RequestFlow(null, null, null, null, new DeliveryRequest("", fr.activity.identifier));
    }

    // Choice Sequencing Request Process [SB.2.9]
    protected sb_2_9(targetActivityId: string): RequestFlow {
        let targetActivity = this.manifest.scosByIdentifiers[targetActivityId];
        // 1
        if (!targetActivityId) {
            return new RequestFlow(null, null, null, null, null, null, false, "SB.2.9-1");
        }

        // 2
        let tmp = targetActivity;
        let activityPath = [];
        while (tmp) {
            activityPath.unshift(tmp);
            tmp = tmp.parent;
        }

        // 3
        for (let i=0; i < activityPath.length; i++) {
            // 3.1
            if (activityPath[i].parent) {
                // 3.1.1
                if (activityPath[i].parent.availableChildren().indexOf(activityPath[i]) == -1) {
                    // 3.1.1.1
                    return new RequestFlow(null, null, null, null, null, null, false, "SB.2.9-2");
                }
            }
            // 3.2
            if (this._debug(this.up_2, [activityPath[i], [RuleActions.HiddenFromChoice]])) {
                // 3.3.1
                return new RequestFlow(null, null, null, null, null, null, false, "SB.2.9-3");
            }
        }

        // 4
        if (targetActivity.identifier != activityPath[0].identifier) {
            // 4.1
            if (!(targetActivity.parent).sequence.controlMode.choice) {
                // 4.1.1
                return new RequestFlow(null, null, null, null, null, null, false, "SB.2.9-4");
            }
        }

        // 5
        let commonAncestor;
        if (this.currentActivityId) {
            // 5.1
            commonAncestor = this.manifest.scosByIdentifiers[this.manifest.commonAncestor(this.currentActivityId, targetActivityId)];
        }
        else {
            // 6.1
            commonAncestor = this.manifest.itemTree;
        }

        let commonAncestorIdx = this.manifest.activityPreorderIndex(commonAncestor);
        let targetActivityIdx = this.manifest.activityPreorderIndex(targetActivity);

        let breakTo13 = false;
        // 7
        if (this.currentActivityId == targetActivityId) {
            // Do nothing.
        }
        // 8
        else if (this.manifest.isSiblings(this.currentActivityId, targetActivityId)) {
            let sibParent = this.manifest.scosByIdentifiers[this.currentActivityId].parent;
            let currentIdx = sibParent.children.indexOf(this.manifest.scosByIdentifiers[this.currentActivityId]);
            let targetIdx = sibParent.children.indexOf(this.manifest.scosByIdentifiers[targetActivityId]);
            // 8.2 isn't possible, skipping intentionally.

            let direction: Direction;
            // 8.3
            if (currentIdx < targetIdx) {
                direction = Direction.Forward;
            }
            // 8.4
            else {
                direction = Direction.Backward;
            }

            // 8.5
            for (let i=currentIdx; i < targetIdx; i++) {
                let fr = this._debug(this.sb_2_4, [sibParent.children[i], Direction.Forward]);
                if (!fr.reachable) {
                    // 3.2.1
                    return new RequestFlow(null, null, null, null, null, null, false, fr.exception);
                }
            }
        }
        // 9
        else if (!this.currentActivityId || this.currentActivityId == commonAncestor.identifier) {
            // 9.2
            if (this.currentActivityId == targetActivityId) {
                return new RequestFlow(null, null, null, null, null, null, false, "SB.2.9-5 (a)");
            }

            // 9.3
            let tmpPath = targetActivity;
            let path = [];
            while (tmpPath.identifier != commonAncestor.identifier) {
                path.push(tmpPath.parent);
                tmpPath = tmpPath.parent;
            }
            path.reverse();

            for (let i=0; i < path.length; i++) {
                let tmpActivity = path[i];
                let fr = this._debug(this.sb_2_4, [tmpActivity, Direction.Forward]);
                if (!fr.reachable) {
                    return new RequestFlow(null, null, null, null, null, null, false, fr.exception);
                }

                // 9.3.3
                if ((tmpActivity.identifier != this.currentActivityId || !this.getCurrentActivityActive()) &&
                    tmpActivity.identifier != commonAncestor.identifier &&
                    tmpActivity.sequence.constrainedChoiceConsiderations.preventActivation
                ) {
                    return new RequestFlow(null, null, null, null, null, null, false, "SB.2.9-6");
                }
            }
        }
        // 10
        else if (targetActivityId == commonAncestor.identifier) {
            let tmpPath = this.currentActivity();
            let path = [tmpPath];
            while (tmpPath.identifier != commonAncestor.identifier) {
                tmpPath = tmpPath.parent;
                path.push(tmpPath);
            }

            // 10.2 is impossible, since it's inclusive.
            // 10.3
            for (let i=0; i < path.length-1; i++) {
                if (!path[i].sequence.controlMode.choiceExit) {
                    return new RequestFlow(null, null, null, null, null, null, false, "SB.2.9-7");
                }
            }

        }
        // 11
        else if (targetActivityIdx > commonAncestorIdx) {
            let tmpPath = this.currentActivity();
            let path = [];
            while (tmpPath.identifier != commonAncestor.identifier) {
                path.push(tmpPath);
                tmpPath = tmpPath.parent;
            }
            // 11.2
            if (path.length == 0) {
                return new RequestFlow(null, null, null, null, null, null, false, "SB.2.9-5 (c)");
            }

            // 11.3
            let constrained = null;

            // 11.4
            for (let i = 0; i > path.length; i++) {
                let tmpActivity = path[i];
                // 11.4.1
                if (!tmpActivity.sequence.controlMode.choiceExit) {
                    return new RequestFlow(null, null, null, null, null, null, false, "SB.2.9-7");
                }
                // 11.4.2
                if (!constrained) {
                    if ((this.manifest.scoPreOrderTraversal[i]).sequence.constrainedChoiceConsiderations.constrainChoice) {
                        constrained = tmpActivity;
                    }
                }
            }
            // 11.5
            if (constrained) {
                let constrainedIdx = this.manifest.activityPreorderIndex(constrained);
                let traverse = Direction.Backward;
                if (targetActivityIdx < constrainedIdx) {
                    traverse = Direction.Forward;
                }
                // 11.5.3 - 11.5.4
                let toConsider = this._debug(this.sb_2_9_1, [constrained, traverse]);
                // 11.5.5
                if (targetActivity.identifier != toConsider.identifier &&
                    targetActivity.identifier != constrained.identifier
                ) {
                    if (targetActivity.isDescendentOf(toConsider)) {
                        return new RequestFlow(null, null, null, null, null, null, false, "SB.2.9-8");
                    }
                }
            }

            // 11.6 - 11.7
            tmpPath = targetActivity;
            path = [];
            while (tmpPath.identifier != commonAncestor.identifier) {
                tmpPath = tmpPath.parent;
                path.push(tmpPath);
            }
            path.reverse();

            if (path.length == 0) {
                return new RequestFlow(null, null, null, null, null, null, false, "SB.2.9-5 (d)");
            }

            // 11.8
            let currentActivityIdx = this.manifest.activityPreorderIndex(this.currentActivity());
            if (targetActivityIdx > currentActivityIdx) {
                for (let i = 0; i < path.length; i++) {
                    // 11.8.1.1
                    let tmpActivity = path[i];
                    let fr = this._debug(this.sb_2_4, [tmpActivity, Direction.Forward]);
                    // 11.8.1.2
                    if (!fr.reachable) {
                        // 11.8.1.2.1
                        return new RequestFlow(null, null, null, null, null, null, false, fr.exception);
                    }
                    // 11.8.1.3
                    if ((tmpActivity.identifier != this.currentActivityId || !this.getCurrentActivityActive()) &&
                        tmpActivity.identifier != commonAncestor.identifier &&
                        tmpActivity.sequence.constrainedChoiceConsiderations.preventActivation) {
                        return new RequestFlow(null, null, null, null, null, null, false, "SB.2.9-6");
                    }
                }
            }
            // 11.9
            else {
                for (let i = 0; i < path.length; i++) {
                    let tmpActivity = path[i];
                    if ((tmpActivity.identifier != this.currentActivityId || !this.getCurrentActivityActive()) &&
                        tmpActivity.identifier != commonAncestor.identifier &&
                        tmpActivity.sequence.constrainedChoiceConsiderations.preventActivation) {
                        // 11.9.1.1.1
                        return new RequestFlow(null, null, null, null, null, null, false, "SB.2.9-6");
                    }
                }
            }
        }

        // 12
        if (targetActivity.isLeaf) {
            return new RequestFlow(null,  null, null, null, new DeliveryRequest("", targetActivityId));
        }

        // 13
        let fr = this._debug(this.sb_2_3, [targetActivity, Direction.Forward, true]);

        // 14
        if (!fr.deliverable) {
            // 14.1
            this._debug(this.up_3, [commonAncestor]);
            // 14.2
            this._debug(this.up_4, [commonAncestor]);
            // 14.3
            this.currentActivityId = targetActivityId;
            // 14.4
            return new RequestFlow(null, null, null, null, null, null, false, "SB.2.9-9");
        }
        // 15
        else {
            return new RequestFlow(null,  null, null, null, new DeliveryRequest("", fr.activity.identifier));
        }
    }

    // Choice Flow Subprocess [SB.2.9.1]
    protected sb_2_9_1(activity: ScoNavItem2004, direction: Direction): ScoNavItem2004 {
        let x = this.sb_2_9_2(activity, direction);
        if (!x) {
            return activity;
        }
        return x;
    }

    // Choice Flow Tree Traversal Subprocess [SB.2.9.2]
    protected sb_2_9_2(activity: ScoNavItem2004, direction: Direction): ScoNavItem2004 {
        // 1
        if (direction == Direction.Forward) {
            // 1.1
            if (activity == this.manifest.scoPreOrderTraversal[this.manifest.scoPreOrderTraversal.length - 1] ||
                activity == this.manifest.itemTree) {
                // 1.1.1
                return null;
            }

            // 1.2
            let availableChildren = activity.parent.availableChildren();
            if (activity == availableChildren[availableChildren.length - 1]) {
                // 1.2.1, 1.2.2
                return this.sb_2_9_2(activity.parent, direction);
            }
            // 1.3
            else {
                let idx = availableChildren.indexOf(activity);
                return availableChildren[idx+1] as ScoNavItem2004;
            }
        }

        // 2
        if (direction == Direction.Backward) {
            // 2.1
            if (activity == this.manifest.itemTree) {
                // 2.1.1
                return null;
            }
            // 2.2
            let availableChildren = activity.parent.availableChildren();
            if (activity == availableChildren[0]) {
                // 2.2.1, 2.2.2
                return this.sb_2_9_2(activity.parent, direction);
            }
            // 2.3
            else {
                let idx = availableChildren.indexOf(activity);
                return availableChildren[idx-1] as ScoNavItem2004;
            }
        }
    }

    // Retry Sequencing Request Process [SB.2.10]
    protected sb_2_10(): RequestFlow {
        // 1
        if (!this.currentActivityId) {
            // 1.1
            return new RequestFlow(null, null, null, null, null, null, false, "SB.2.10-1");
        }
        // 2
        if (this.getCurrentActivityActive() || this.currentActivityId == this.suspendedActivityId) {
            // 2.1
            return new RequestFlow(null, null, null, null, null, null, false, "SB.2.10-2");
        }
        // 3
        if (this.currentActivity().children.length > 0) {
            // 3.1
            let fr: FlowReturn = this.sb_2_3(this.manifest.scosByIdentifiers[this.currentActivityId], Direction.Backward, true);
            if (fr.exception) {
                // 3.2.1
                return new RequestFlow(null, null, null, null, null, null, false, "SB.2.10-3");
            }
            // 3.3.1
            return new RequestFlow(null, null, null, null, new DeliveryRequest("", fr.activity.identifier));
        }
        else {
            // 4.1
            return new RequestFlow(null, null, null, null, new DeliveryRequest("", this.currentActivityId));
        }
    }

    // Exit Sequencing Request Process [SB.2.11]
    protected sb_2_11(): RequestFlow {
        // 1
        if (!this.currentActivityId) {
            // 1.1
            return new RequestFlow(null, null, null, null, null, null, false, "SB.2.11-1");
        }
        // 2
        if (this.getCurrentActivityActive()) {
            // 2.1
            return new RequestFlow(null, null, null, null, null, null, false, "SB.2.11-2");
        }
        // 3
        if (this.currentActivityId == this.manifest.itemTree.identifier) {
            // 3.1
            return new RequestFlow(null, null, null, null, null, null, true);
        }
        // 4
        return new RequestFlow(null, null, null, null, null, null, false);
    }

    // Sequencing Request Process [SB.2.12]
    public sb_2_12(seqRequest: SequencingRequest): RequestFlow {
        let rf: RequestFlow;
        if (seqRequest.actions == SeqActions.Start) {
            rf = this._debug(this.sb_2_5);
        }

        // 2
        if (seqRequest.actions == SeqActions.ResumeAll) {
            rf = this._debug(this.sb_2_6);
        }

        // 3
        if (seqRequest.actions == SeqActions.Exit) {
            rf = this._debug(this.sb_2_11);
        }

        // 4
        if (seqRequest.actions == SeqActions.Retry) {
            rf = this._debug(this.sb_2_10);
        }

        // 5
        if (seqRequest.actions == SeqActions.Continue) {
            rf = this._debug(this.sb_2_7);
        }

        // 6
        if (seqRequest.actions == SeqActions.Previous) {
            rf = this._debug(this.sb_2_8);
        }

        // 7
        if (seqRequest.actions == SeqActions.Choice) {
            rf = this._debug(this.sb_2_9, [seqRequest.target]);
        }

        // TODO Disable for 2004 3rd Edition
        // 8 in 2004 4th Edition
        if (seqRequest.actions == SeqActions.Jump) {
            rf = this._debug(this.sb_2_13, [seqRequest.target]);
        }

        if (rf != null) {
            if (rf.exception) {
                return new RequestFlow(STATUS_NOT_VALID, null, null, null, null, null, false, rf.exception);
            }
            return new RequestFlow(STATUS_VALID, null, null, null, rf.deliveryReq, null, rf.endSequencingSession, "");
        }

        // 8 in 2004 3rd Edition, 9 in 2004 4th Edition
        return new RequestFlow(null, null, null, null, null, null, false, "SB.2.12-1");
    }

    // Jump Sequencing Request Process [SB.2.13]
    protected sb_2_13(targetActivityId: string): RequestFlow {
        // 1
        if (!this.currentActivityId) {
            // 1.1
            return new RequestFlow(null, null, null, null, null, null, false, "SB.2.13-1");
        }
        // 2
        return new RequestFlow(null, null, null, null, new DeliveryRequest("", targetActivityId));
    }

    // Delivery Request Process [DB.1.1]
    protected db_1_1(req: Request): RequestFlow {
        let activity: ScoNavItem2004 = this.manifest.scosByIdentifiers[req.target];
        if (!activity.isLeaf) {
            return new RequestFlow(STATUS_NOT_VALID, null, null, null, null, null, false, "DB.1.1-1");
        }
        let scos: ScoNavItem2004[] = activity.getLineage() as ScoNavItem2004[];
        if (scos.length == 0) {
            return new RequestFlow(STATUS_NOT_VALID, null, null, null, null, null, false, "DB.1.1-2");
        }
        for (let i: number = 0; i < scos.length; i++) {
            if (this._debug(this.up_5, [scos[i]])) {
                return new RequestFlow(STATUS_NOT_VALID, null, null, null, null, null, false, "DB.1.1-3");
            }
        }
        return new RequestFlow(STATUS_VALID, null, null, null);
    }

    // Content Delivery Environment Process [DB.2]
    protected db_2(req: Request): string {
        // 1
        if (this.getCurrentActivityActive()) {
            return "DB.2-1";
        }

        // 2
        let tmpActivity = this.manifest.scosByIdentifiers[req.target];
        if (req.target != this.suspendedActivityId) {
            this._debug(this.db_2_1, [tmpActivity]);
        }

        // 3
        this._debug(this.up_4, [tmpActivity]);

        // 4
        let path = [tmpActivity];
        while (tmpActivity.parent != null) {
            path.push(tmpActivity.parent);
            tmpActivity = tmpActivity.parent;
        }
        path.reverse();

        // 5
        for (let i=0; i < path.length; i++) {
            // TODO Does every activity truly have an independent suspended state in this context?
            // 5.1.1
            if (path[i].sequence.deliveryControls.tracked) {
                if (this.suspendedActivityId == path[i].identifier) {
                    // 5.1.1.1.1
                    this.suspendedActivityId = "";
                }
                else {
                    // TODO 5.1.1.2.*. Increment activity attempt account and set progress status to true and initialize new attempt info.
                }
            }
            // 5.1.2
            this.setCurrentActivityActive(true);
        }

        // 6
        this.currentActivityId = req.target;

        // 7
        this.suspendedActivityId = "";

        // TODO Is this the right place to init local objectives?
        this.initLocalObjectives(req.target);

        return req.target;
    }

    // Clear Suspended Activity Subprocess [DB.2.1]
    protected db_2_1(activity: ScoNavItem2004) {
        // TODO This is effectively a NOOP unless we have to maintain suspended state on lots of activities.
        // What's the overlap between suspended state and cmi.exit = "suspend" for the context of a cluster?
    }

    protected initLocalObjectives(activityId: string): void {
        let activity: ScoNavItem2004 = this.manifest.scosByIdentifiers[activityId];
        if (activity.sequence.primaryObjective) {
            // Set up the primary objective.
            if (!this.scosData.getOrCreate(activityId).get("cmi.objectives.0.id")) {
                this.scosData.getOrCreate(activityId).set("cmi.objectives.0.id", activity.sequence.primaryObjective.objectiveId);
                // TODO: Init the statuses too.
            }

            // Set up any secondary objectives.
            for (let i: number = 0; i < activity.sequence.objectives.length; i++) {
                if (!this.scosData.getOrCreate(activityId).get("cmi.objectives." + (i + 1) + ".id")) {
                    this.scosData.getOrCreate(activityId).set("cmi.objectives." + (i + 1) + ".id", activity.sequence.objectives[i].objectiveId);
                }
                // TODO: Init the statuses too.
            }
        }
    }

    // Limit Conditions Check Process [UP.1]
    protected up_1(activity: ScoNavItem2004): boolean {
        if (!activity.sequence.deliveryControls.tracked) {
            return false;
        }
        if (this.getCurrentActivityActive() || this.suspendedActivityId == activity.identifier) {
            return false;
        }
        return false;
        // TODO Limit condition attempt control.

        // Note: None of the other items are required.
    }

    // Sequencing Rules Check Process [UP.2]
    protected up_2(activity: ScoNavItem2004, actions: string[]): string | null {
        let rules: SequenceRule[][] = [activity.sequence.preConditionRules,
            activity.sequence.postConditionRules,
            activity.sequence.exitConditionRules
        ];
        for (let rType: number = 0; rType < rules.length; rType++) {
            for (let i: number = 0; i < rules[rType].length; i++) {
                this._debugEntry("rulecheck", "i: " + i + " rule action: "+ rules[rType][i].action);
                for (let actionIdx: number = 0; actionIdx < actions.length; actionIdx++) {
                    this._debugEntry("rulecheck", "actionIdx: " + i + " action: " + actions[actionIdx]);
                    if (rules[rType][i].action == actions[actionIdx]) {
                        if (this._debug(this.up_2_1, [activity, rules[rType][i]]) == "true") {
                            return rules[rType][i].action;
                        }
                    }
                }
            }
        }
        return null;
    }

    protected globalObjectiveIdFromLocalObjectiveId(activity: ScoNavItem2004, objectiveId: string): string {
        if (objectiveId == "") {
            if (activity.sequence.primaryObjective) {
                return this.globalIdFromLocalObjective(activity.sequence.primaryObjective);
            }
            return "";
        }
        for (let i: number = 0; i < activity.sequence.objectives.length; i++) {
            if (activity.sequence.objectives[i].objectiveId == objectiveId) {
                return this.globalIdFromLocalObjective(activity.sequence.objectives[i]);
            }
        }
        return "";
    }

    protected getGlobalObjectiveSuccessStatus(globalObjectiveId: string): string {
        if (globalObjectiveId != "") {
            let go = this.scosData.globalObjectives[globalObjectiveId];
            if (go) {
                return go.successStatus;
            }
        }
        return "unknown";
    }

    // Sequencing Rule Check Subprocess [UP.2.1]
    protected up_2_1(activity: ScoNavItem2004, rule: SequenceRule): string {
        let i: number;
        let results: boolean[] = [];
        let scoData: Sco2004Data = this.scosData.getOrCreate(activity.identifier) as Sco2004Data;
        for (i = 0; i < rule.conditions.length; i++) {
            let result: boolean = null;

            if (rule.conditions[i].condition == "satisfied") {
                let globalObjectiveId: string = this.globalObjectiveIdFromLocalObjectiveId(activity, rule.conditions[i].referencedObjective);
                let status: string = this.getGlobalObjectiveSuccessStatus(globalObjectiveId);
                result = status == "passed";
            }
            else if (rule.conditions[i].condition == "objectiveStatusKnown") {
                let globalObjectiveId: string = this.globalObjectiveIdFromLocalObjectiveId(activity, rule.conditions[i].referencedObjective);
                if (globalObjectiveId != "") {
                    let status: string = this.getGlobalObjectiveSuccessStatus(globalObjectiveId);
                    result = status == "passed" || status == "failed";
                }
            }
            else if (rule.conditions[i].condition == "objectiveMeasureKnown") {
                let globalObjectiveId: string = this.globalObjectiveIdFromLocalObjectiveId(activity, rule.conditions[i].referencedObjective);
                if (globalObjectiveId != "") {
                    let measure: string = this.scosData.globalObjectives[globalObjectiveId].scaled;
                    if (measure != "") {
                        result = true;
                    }
                    else {
                        result = false;
                    }
                }
            }
            else if (rule.conditions[i].condition == "objectiveMeasureGreaterThan") {
                let globalObjectiveId: string = this.globalObjectiveIdFromLocalObjectiveId(activity, rule.conditions[i].referencedObjective);
                if (globalObjectiveId != "") {
                    let measure: string = this.scosData.globalObjectives[globalObjectiveId].scaled;
                    if (measure != "") {
                        let measureVal: number = parseFloat(measure as string);
                        if (measureVal > rule.conditions[i].measureThreshold) {
                            result = true;
                        }
                        else {
                            result = false;
                        }
                    }
                }
            }
            else if (rule.conditions[i].condition == "objectiveMeasureLessThan") {
                let globalObjectiveId: string = this.globalObjectiveIdFromLocalObjectiveId(activity, rule.conditions[i].referencedObjective);
                if (globalObjectiveId != "") {
                    let measure: string = this.scosData.globalObjectives[globalObjectiveId].scaled;
                    if (measure != "") {
                        let measureVal: number = parseFloat(measure as string);
                        if (measureVal < rule.conditions[i].measureThreshold) {
                            result = true;
                        }
                        else {
                            result = false;
                        }
                    }
                }
            }
            else if (rule.conditions[i].condition == "completed") {
                let globalObjectiveId: string = this.globalObjectiveIdFromLocalObjectiveId(activity, rule.conditions[i].referencedObjective);
                if (globalObjectiveId != "") {
                    let status: string = this.scosData.globalObjectives[globalObjectiveId].completionStatus;
                    if (status == "completed") {
                        result = true;
                    }
                    else {
                        result = false;
                    }
                }
            }
            else if (rule.conditions[i].condition == "activityProgressKnown") {
                let globalObjectiveId: string = this.globalObjectiveIdFromLocalObjectiveId(activity, rule.conditions[i].referencedObjective);
                if (globalObjectiveId != "") {
                    let status: string = this.scosData.globalObjectives[globalObjectiveId].completionStatus;
                    if (status == "completed" || status == "incomplete") {
                        result = true;
                    }
                    else {
                        result = false;
                    }
                }
            }
            else if (rule.conditions[i].condition == "attempted") {
                let globalObjectiveId: string = this.globalObjectiveIdFromLocalObjectiveId(activity, rule.conditions[i].referencedObjective);
                if (globalObjectiveId != "") {
                    let status: string = this.scosData.globalObjectives[globalObjectiveId].completionStatus;
                    if (status == "completed" || status == "incomplete") {
                        result = true;
                    }
                    else {
                        result = false;
                    }
                }
            }
            else if (rule.conditions[i].condition == "attemptLimitExceeded") {
                // TODO
            }
            else if (rule.conditions[i].condition == "timeLimitExceeded") {
                // TODO
            }
            else if (rule.conditions[i].condition == "outsideAvailableTimeRange") {
                // TODO
            }
            else if (rule.conditions[i].condition == "always") {
                result = true;
            }

            // TODO: Const this.
            if (rule.conditions[i].operator == "not") {
                if (result === true) {
                    result = false;
                }
                else if (result === false) {
                    result = true;
                }
            }
            results.push(result);
        }
        if (results.length > 0) {
            if (rule.conditionCombination == "any") {
                for (i = 0; i < results.length; i++) {
                    if (results[i] === true) {
                        return "true";
                    }
                }
                return "false";
            }
            else if (rule.conditionCombination == "all") {
                for (i = 0; i < results.length; i++) {
                    if (results[i] !== true) {
                        return "false";
                    }
                }
                return "true";
            }
        }
        return "unknown";
    }

    // Terminate Descendent Attempts Process [UP.3]
    protected up_3(activity: ScoNavItem2004): void {
        let ancestor = this.manifest.commonAncestor(activity.identifier, this.currentActivityId);
        let tmp = this.currentActivity();
        let path = [];
        while (tmp.identifier != ancestor) {
            tmp = tmp.parent;
            if (tmp.identifier != ancestor) {
                path.push(tmp);
            }
        }
        for (let i=0; i < path.length; i++) {
            this._debug(this.up_4, [path[i]]);
        }
    }

    // End Attempt Process [UP.4]
    protected up_4(activity: ScoNavItem2004): void {
        // 1
        if (activity.isLeaf) {
            // 1.1
            if (activity.sequence.deliveryControls.tracked) {
                // 1.1.1
                if (this.scosData.getOrCreate(activity.identifier).get("cmi.exit") != "suspend") {
                    // 1.1.1.1
                    if (!activity.sequence.deliveryControls.completionSetByContent) {
                        // 1.1.1.1.1.1 - 1.1.1.1.1.2
                        this.scosData.getOrCreate(activity.identifier).set("cmi.completion_status", "completed")
                    }

                    // 1.1.1.2
                    if (!activity.sequence.deliveryControls.objectiveSetByContent) {
                        // ADL Note: Although this element is defined in the IMS SS Sequencing Definition Model,
                        // it cannot be directly set or altered. The XML element <primaryObjective> in a
                        // SCORM Content Package (see the SCORM CAM Book, Section 5.1.7.1: <primaryObjective> Element)
                        // is used to indicate the single objective that contributes to rollup.
                        // For an activity, only the objective defined as the <primaryObjective> will contribute
                        // to rollup (have Objective Contributes to Rollup equal to True).
                        // 1.1.1.2.1.1
                        if (activity.sequence.primaryObjective) {
                            // 1.1.1.2.1.1.1
                            if (this.scosData.getOrCreate(activity.identifier).get("cmi.objectives.0.success_status") == "unknown") {
                                // 1.1.1.2.1.1.1.1 - 1.1.1.2.1.1.1.2
                                this.scosData.getOrCreate(activity.identifier).set("cmi.objectives.0.success_status", "passed");
                            }
                        }
                    }
                }
            }
        }
        else {
            // 2
            // TODO This is effectively a NOOP unless we have to maintain suspended state on lots of activities.
            // What's the overlap between suspended state and cmi.exit = "suspend" for the context of a cluster?
        }

        // 3
        this.setCurrentActivityActive(false);
        // 4
        this._debug(this.rb_1_5, [activity]);
    }

    // Check Activity Process [UP.5]
    public up_5(activity: ScoNavItem2004): boolean {
        let ruleCheck = this._debug(this.up_2, [activity, [RuleActions.Disabled]]);
        if (ruleCheck != null) {
            return true;
        }
        return this._debug(this.up_1, [activity]);
    }

}